import React, { useState } from 'react'
import { useRestaurants, useSuspendRestaurant, useActivateRestaurant } from '@/hooks/admin/useRestaurants'
import { RestaurantStatus, SubscriptionPlan } from '@/types/admin'
import type { RestaurantListDTO } from '@/types/admin'
import type { RestaurantsPageResponse } from '@/lib/api/admin/restaurants'
import {
  Store,
  Search,
  Filter,
  Edit,
  Ban,
  CheckCircle,
  Trash2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { formatCurrency } from '@/utils/currency'
import { EditRestaurantModal } from '@/components/admin/EditRestaurantModal'
import { ChangePlanModal } from '@/components/admin/ChangePlanModal'
import { DeleteRestaurantModal } from '@/components/admin/DeleteRestaurantModal'

const STATUS_LABELS: Record<RestaurantStatus, string> = {
  ACTIVE: 'Attivo',
  SUSPENDED: 'Sospeso',
  CLOSED: 'Chiuso',
}

const STATUS_COLORS: Record<RestaurantStatus, string> = {
  ACTIVE: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  SUSPENDED: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  CLOSED: 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300',
}

const PLAN_COLORS: Record<SubscriptionPlan, string> = {
  FREE: 'text-gray-600 dark:text-gray-400',
  STARTER: 'text-blue-600 dark:text-blue-400',
  PRO: 'text-purple-600 dark:text-purple-400',
  ENTERPRISE: 'text-yellow-600 dark:text-yellow-400',
}

export default function AdminRestaurantsPage() {
  const [page, setPage] = useState(0)
  const [size] = useState(20)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<RestaurantStatus | ''>('')
  const [planFilter, setPlanFilter] = useState<SubscriptionPlan | ''>('')
  const [searchInput, setSearchInput] = useState('')

  // Modals
  const [editingRestaurant, setEditingRestaurant] = useState<RestaurantListDTO | null>(null)
  const [changingPlanRestaurant, setChangingPlanRestaurant] = useState<RestaurantListDTO | null>(null)
  const [deletingRestaurant, setDeletingRestaurant] = useState<RestaurantListDTO | null>(null)

  const { data, isLoading, error } = useRestaurants({
    page,
    size,
    status: statusFilter || undefined,
    plan: planFilter || undefined,
    search: search || undefined,
  }) as { data?: RestaurantsPageResponse; isLoading: boolean; error: Error | null }

  const suspendMutation = useSuspendRestaurant()
  const activateMutation = useActivateRestaurant()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(0)
  }

  const handleSuspend = async (id: number) => {
    if (confirm('Sei sicuro di voler sospendere questo ristorante?')) {
      try {
        await suspendMutation.mutateAsync(id)
      } catch (error) {
        console.error('Failed to suspend restaurant:', error)
        alert('Errore durante la sospensione del ristorante')
      }
    }
  }

  const handleActivate = async (id: number) => {
    try {
      await activateMutation.mutateAsync(id)
    } catch (error) {
      console.error('Failed to activate restaurant:', error)
      alert('Errore durante l\'attivazione del ristorante')
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Gestione Ristoranti
        </h1>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <p className="text-red-600 dark:text-red-400">
            Errore nel caricamento ristoranti: {error.message}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestione Ristoranti
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {data ? `${data.totalElements} ristoranti totali` : 'Caricamento...'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Store className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cerca per nome..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as RestaurantStatus | '')
                setPage(0)
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="">Tutti gli stati</option>
              <option value="ACTIVE">Attivi</option>
              <option value="SUSPENDED">Sospesi</option>
              <option value="CLOSED">Chiusi</option>
            </select>
          </div>

          {/* Plan Filter */}
          <div className="relative">
            <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value as SubscriptionPlan | '')
                setPage(0)
              }}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
            >
              <option value="">Tutti i piani</option>
              <option value="FREE">FREE</option>
              <option value="STARTER">STARTER</option>
              <option value="PRO">PRO</option>
              <option value="ENTERPRISE">ENTERPRISE</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-4">Caricamento ristoranti...</p>
          </div>
        ) : !data || data.content.length === 0 ? (
          <div className="p-12 text-center">
            <Store className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {search || statusFilter || planFilter
                ? 'Nessun ristorante trovato con i filtri selezionati'
                : 'Nessun ristorante presente'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ristorante
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Piano
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      MRR
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ordini (30gg)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.content.map((restaurant) => (
                    <tr
                      key={restaurant.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {restaurant.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {restaurant.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-semibold ${PLAN_COLORS[restaurant.currentPlan]}`}>
                          {restaurant.currentPlan}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_COLORS[restaurant.status]
                          }`}
                        >
                          {STATUS_LABELS[restaurant.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                        {formatCurrency(restaurant.monthlyRecurringRevenue)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900 dark:text-white">
                        {restaurant.totalOrdersLast30Days}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setEditingRestaurant(restaurant)}
                            className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setChangingPlanRestaurant(restaurant)}
                            className="p-1.5 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
                            title="Cambia Piano"
                          >
                            <CreditCard className="w-4 h-4" />
                          </button>
                          {restaurant.status === 'ACTIVE' ? (
                            <button
                              onClick={() => handleSuspend(restaurant.id)}
                              className="p-1.5 text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                              title="Sospendi"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          ) : restaurant.status === 'SUSPENDED' ? (
                            <button
                              onClick={() => handleActivate(restaurant.id)}
                              className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                              title="Attiva"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          ) : null}
                          <button
                            onClick={() => setDeletingRestaurant(restaurant)}
                            className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Pagina {data.number + 1} di {data.totalPages} • {data.totalElements} risultati
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= data.totalPages - 1}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {editingRestaurant && (
        <EditRestaurantModal
          restaurant={editingRestaurant}
          onClose={() => setEditingRestaurant(null)}
        />
      )}
      {changingPlanRestaurant && (
        <ChangePlanModal
          restaurant={changingPlanRestaurant}
          onClose={() => setChangingPlanRestaurant(null)}
        />
      )}
      {deletingRestaurant && (
        <DeleteRestaurantModal
          restaurant={deletingRestaurant}
          onClose={() => setDeletingRestaurant(null)}
        />
      )}
    </div>
  )
}
